import { useState } from "react";
import type { Interviewer } from "../../hooks";
import { useInterviewerMutations, useInterviewers } from "../../hooks";
import { logger } from "../../lib/logger";

type RoleSelection = "admin" | "interviewer";

export function Users() {
  const { interviewers, loading, error, refetch } = useInterviewers();
  const { createInterviewer, updateInterviewer, deleteInterviewer } =
    useInterviewerMutations();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [formData, setFormData] = useState({
    email: "",
    name: "",
    role: "interviewer" as RoleSelection,
    isDefault: false,
    isKeyInterviewer: false,
  });
  const [creating, setCreating] = useState(false);
  const [deleting, setDeleting] = useState<string | null>(null);

  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingInterviewer, setEditingInterviewer] =
    useState<Interviewer | null>(null);
  const [editFormData, setEditFormData] = useState({
    name: "",
    role: "interviewer" as RoleSelection,
    isDefault: false,
    isKeyInterviewer: false,
  });
  const [editing, setEditing] = useState(false);

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    setCreating(true);
    try {
      const result = await createInterviewer({
        email: formData.email,
        name: formData.name.trim(),
        isAdmin: formData.role === "admin",
        isDefault: formData.isDefault,
        isKeyInterviewer: formData.isKeyInterviewer,
      });
      if (result) {
        setFormData({
          email: "",
          name: "",
          role: "interviewer",
          isDefault: false,
          isKeyInterviewer: false,
        });
        setDialogOpen(false);
        refetch();
      } else {
        alert("作成に失敗しました");
      }
    } catch (err) {
      logger.error(err as Error);
      alert("作成に失敗しました");
    } finally {
      setCreating(false);
    }
  };

  const handleOpenEditDialog = (interviewer: Interviewer) => {
    setEditingInterviewer(interviewer);
    setEditFormData({
      name: interviewer.name,
      role: interviewer.isAdmin ? "admin" : "interviewer",
      isDefault: interviewer.isDefault,
      isKeyInterviewer: interviewer.isKeyInterviewer,
    });
    setEditDialogOpen(true);
  };

  const handleEditSave = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingInterviewer) return;
    setEditing(true);
    try {
      const result = await updateInterviewer({
        interviewerId: editingInterviewer.interviewerId,
        name: editFormData.name.trim(),
        isAdmin: editFormData.role === "admin",
        isDefault: editFormData.isDefault,
        isKeyInterviewer: editFormData.isKeyInterviewer,
      });
      if (result) {
        setEditDialogOpen(false);
        setEditingInterviewer(null);
        refetch();
      } else {
        alert("更新に失敗しました");
      }
    } catch (err) {
      logger.error(err as Error);
      alert("更新に失敗しました");
    } finally {
      setEditing(false);
    }
  };

  const handleDelete = async (interviewer: Interviewer) => {
    if (
      !confirm(
        `${interviewer.name} を削除しますか？\n※関連するインタビューがある場合は削除できません。`,
      )
    ) {
      return;
    }

    setDeleting(interviewer.interviewerId);
    try {
      const result = await deleteInterviewer(interviewer.interviewerId);
      if (result.ok) {
        refetch();
      } else {
        alert(result.message);
      }
    } catch (err) {
      logger.error(err as Error);
      alert("削除に失敗しました");
    } finally {
      setDeleting(null);
    }
  };

  return (
    <div className="min-h-[calc(100vh-200px)] bg-[var(--color-bg-subtle-gray)] p-6">
      <div className="mx-auto max-w-[1200px]">
        <div className="mb-4">
          <nav className="flex items-center gap-2 text-[13px] text-[var(--color-text-secondary)]">
            <span className="font-medium text-[var(--color-text-primary)]">
              インタビュアー
            </span>
          </nav>
        </div>

        <div className="mb-8">
          <div className="flex flex-row items-start justify-between gap-4">
            <div className="border-[var(--color-primary-500)] border-l-4 pl-5">
              <h1 className="mb-2 font-bold text-[32px] text-[var(--color-primary-500)]">
                インタビュアー管理
              </h1>
              <p className="font-medium text-[14px] text-[var(--color-text-secondary)]">
                インタビュアーの作成・管理
              </p>
            </div>
            <button
              type="button"
              onClick={() => setDialogOpen(true)}
              className="flex cursor-pointer items-center gap-2 rounded-md border-none bg-[var(--color-primary-500)] px-4 py-2.5 font-medium text-[14px] text-white hover:bg-[var(--color-primary-600)]"
            >
              <svg
                className="h-4 w-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 4v16m8-8H4"
                />
              </svg>
              新規作成
            </button>
          </div>
        </div>

        {loading ? (
          <div className="overflow-hidden rounded-xl border border-[var(--color-border-default)] bg-white shadow-[0_2px_8px_rgba(0,0,0,0.05)]">
            <div className="flex items-center justify-center p-12">
              <div className="flex flex-col items-center gap-4">
                <div className="h-8 w-8 animate-spin rounded-full border-4 border-[var(--color-border-default)] border-t-[var(--color-primary-500)]" />
                <p className="font-medium text-[var(--color-text-secondary)]">
                  読み込み中...
                </p>
              </div>
            </div>
          </div>
        ) : error ? (
          <div className="overflow-hidden rounded-xl border border-[var(--color-red-100)] bg-[var(--color-red-50)] p-6">
            <p className="font-medium text-[var(--color-red-600)]">
              ユーザー一覧の読み込みに失敗しました
            </p>
            <p className="mt-2 text-[var(--color-red-600)] text-sm">{error}</p>
          </div>
        ) : interviewers.length === 0 ? (
          <div className="overflow-hidden rounded-xl border border-[var(--color-border-default)] bg-white shadow-[0_2px_8px_rgba(0,0,0,0.05)]">
            <div className="flex flex-col items-center justify-center p-12">
              <svg
                className="mb-4 h-12 w-12 text-[#9ca3af]"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z"
                />
              </svg>
              <p className="mb-4 text-[#9ca3af]">
                インタビュアーが登録されていません
              </p>
              <button
                type="button"
                onClick={() => setDialogOpen(true)}
                className="flex cursor-pointer items-center gap-2 rounded-md border-none bg-[var(--color-primary-500)] px-4 py-2.5 font-medium text-[14px] text-white hover:bg-[var(--color-primary-600)]"
              >
                <svg
                  className="h-4 w-4"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 4v16m8-8H4"
                  />
                </svg>
                最初のインタビュアーを作成
              </button>
            </div>
          </div>
        ) : (
          <div className="overflow-hidden rounded-xl border border-[var(--color-border-default)] bg-white shadow-[0_2px_8px_rgba(0,0,0,0.05)]">
            <div className="border-[var(--color-primary-500)] border-b-2 bg-[var(--color-bg-subtle-gray)] px-6 py-5">
              <h2 className="font-bold text-[24px] text-[var(--color-primary-500)]">
                一覧
              </h2>
            </div>
            <div className="p-6">
              <table className="w-full border-collapse">
                <thead>
                  <tr>
                    <th className="w-20 px-4 py-3 text-left font-semibold text-[13px] text-[var(--color-text-secondary)]">
                      ID
                    </th>
                    <th className="px-4 py-3 text-left font-semibold text-[13px] text-[var(--color-text-secondary)]">
                      名前
                    </th>
                    <th className="px-4 py-3 text-left font-semibold text-[13px] text-[var(--color-text-secondary)]">
                      メールアドレス
                    </th>
                    <th className="w-[100px] px-4 py-3 text-left font-semibold text-[13px] text-[var(--color-text-secondary)]">
                      ロール
                    </th>
                    <th className="w-[100px] px-4 py-3 text-left font-semibold text-[13px] text-[var(--color-text-secondary)]">
                      デフォルト
                    </th>
                    <th className="w-[100px] px-4 py-3 text-left font-semibold text-[13px] text-[var(--color-text-secondary)]">
                      キー
                    </th>
                    <th className="w-[120px] px-4 py-3 text-left font-semibold text-[13px] text-[var(--color-text-secondary)]">
                      登録日
                    </th>
                    <th className="w-[140px] px-4 py-3 text-left font-semibold text-[13px] text-[var(--color-text-secondary)]">
                      操作
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {interviewers.map((interviewer) => {
                    const roleSelection: RoleSelection = interviewer.isAdmin
                      ? "admin"
                      : "interviewer";

                    return (
                      <tr
                        key={interviewer.interviewerId}
                        className="border-[var(--color-border-default)] border-t hover:bg-[var(--color-bg-subtle-gray)]"
                      >
                        <td className="px-4 py-3 text-[14px] text-[var(--color-text-secondary)]">
                          {interviewer.interviewerId.slice(-8)}
                        </td>
                        <td className="px-4 py-3 font-medium text-[14px] text-[var(--color-text-primary)]">
                          {interviewer.name}
                        </td>
                        <td className="px-4 py-3 text-[14px] text-[var(--color-text-primary)]">
                          {interviewer.email}
                        </td>
                        <td className="px-4 py-3">
                          <span
                            className="rounded-md px-2.5 py-1 font-medium text-[12px]"
                            style={{
                              backgroundColor:
                                roleSelection === "admin"
                                  ? "#dbeafe"
                                  : "#dcfce7",
                              color:
                                roleSelection === "admin"
                                  ? "#1e40af"
                                  : "#166534",
                            }}
                          >
                            {roleSelection === "admin"
                              ? "管理者"
                              : "インタビュアー"}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          <span
                            className="rounded-md px-2.5 py-1 font-medium text-[12px]"
                            style={{
                              backgroundColor: interviewer.isDefault
                                ? "#dcfce7"
                                : "#f3f4f6",
                              color: interviewer.isDefault
                                ? "#166534"
                                : "#9ca3af",
                            }}
                          >
                            {interviewer.isDefault ? "ON" : "OFF"}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          <span
                            className="rounded-md px-2.5 py-1 font-medium text-[12px]"
                            style={{
                              backgroundColor: interviewer.isKeyInterviewer
                                ? "#fef3c7"
                                : "#f3f4f6",
                              color: interviewer.isKeyInterviewer
                                ? "#92400e"
                                : "#9ca3af",
                            }}
                          >
                            {interviewer.isKeyInterviewer ? "ON" : "OFF"}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-[14px] text-[var(--color-text-secondary)]">
                          {interviewer.createdAt
                            ? new Date(
                                interviewer.createdAt,
                              ).toLocaleDateString("ja-JP")
                            : "-"}
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex gap-2">
                            <button
                              type="button"
                              onClick={() => handleOpenEditDialog(interviewer)}
                              className="cursor-pointer rounded border border-[var(--color-primary-500)] bg-white px-3 py-1.5 font-medium text-[12px] text-[var(--color-primary-500)]"
                            >
                              編集
                            </button>
                            <button
                              type="button"
                              onClick={() => handleDelete(interviewer)}
                              disabled={deleting === interviewer.interviewerId}
                              style={{
                                backgroundColor:
                                  deleting === interviewer.interviewerId
                                    ? "#fecaca"
                                    : "#fff",
                                cursor:
                                  deleting === interviewer.interviewerId
                                    ? "not-allowed"
                                    : "pointer",
                              }}
                              className="rounded border border-[#dc2626] px-3 py-1.5 font-medium text-[#dc2626] text-[12px]"
                            >
                              {deleting === interviewer.interviewerId
                                ? "削除中..."
                                : "削除"}
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      {dialogOpen && (
        <div
          className="fixed inset-0 z-[1000] flex items-center justify-center bg-black/50"
          onClick={() => setDialogOpen(false)}
        >
          <div
            className="w-full max-w-[450px] rounded-xl bg-white p-6 shadow-[0_4px_20px_rgba(0,0,0,0.15)]"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="mb-5 font-semibold text-[18px] text-[var(--color-text-primary)]">
              インタビュアーを作成
            </h3>
            <form onSubmit={handleCreate}>
              <div className="mb-4">
                <label
                  htmlFor="email"
                  className="mb-1.5 block font-medium text-[14px] text-[var(--color-text-primary)]"
                >
                  メールアドレス
                </label>
                <input
                  id="email"
                  type="email"
                  value={formData.email}
                  onChange={(e) =>
                    setFormData({ ...formData, email: e.target.value })
                  }
                  placeholder="example@mento.co.jp"
                  required
                  className="box-border w-full rounded-md border border-[var(--color-border-default)] px-3 py-2.5 text-[14px] outline-none"
                />
              </div>
              <div className="mb-4">
                <label
                  htmlFor="name"
                  className="mb-1.5 block font-medium text-[14px] text-[var(--color-text-primary)]"
                >
                  名前
                </label>
                <input
                  id="name"
                  type="text"
                  required
                  value={formData.name}
                  onChange={(e) =>
                    setFormData({ ...formData, name: e.target.value })
                  }
                  placeholder="山田 太郎"
                  className="box-border w-full rounded-md border border-[var(--color-border-default)] px-3 py-2.5 text-[14px] outline-none"
                />
              </div>
              <div className="mb-5">
                <label className="mb-2 block font-medium text-[14px] text-[var(--color-text-primary)]">
                  ロール
                </label>
                <div className="flex flex-col gap-2">
                  <label className="flex cursor-pointer items-center gap-2">
                    <input
                      type="radio"
                      name="create-role"
                      checked={formData.role === "admin"}
                      onChange={() =>
                        setFormData({ ...formData, role: "admin" })
                      }
                      className="accent-[var(--color-primary-500)]"
                    />
                    <span className="text-[14px]">管理者</span>
                  </label>
                  <label className="flex cursor-pointer items-center gap-2">
                    <input
                      type="radio"
                      name="create-role"
                      checked={formData.role === "interviewer"}
                      onChange={() =>
                        setFormData({ ...formData, role: "interviewer" })
                      }
                      className="accent-[var(--color-primary-500)]"
                    />
                    <span className="text-[14px]">インタビュアー</span>
                  </label>
                </div>
              </div>
              <div className="mb-5">
                <label className="flex cursor-pointer items-center gap-2">
                  <input
                    type="checkbox"
                    checked={formData.isDefault}
                    onChange={(e) =>
                      setFormData({ ...formData, isDefault: e.target.checked })
                    }
                    className="accent-[var(--color-primary-500)]"
                  />
                  <span className="font-medium text-[14px]">
                    デフォルトインタビュアーに設定
                  </span>
                </label>
              </div>
              <div className="mb-5">
                <label className="flex cursor-pointer items-center gap-2">
                  <input
                    type="checkbox"
                    checked={formData.isKeyInterviewer}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        isKeyInterviewer: e.target.checked,
                      })
                    }
                    className="accent-[var(--color-primary-500)]"
                  />
                  <span className="font-medium text-[14px]">
                    キーインタビュアーに設定
                  </span>
                </label>
              </div>
              <div className="flex justify-end gap-3">
                <button
                  type="button"
                  onClick={() => setDialogOpen(false)}
                  className="cursor-pointer rounded-md border border-[var(--color-text-secondary)] bg-white px-4 py-2.5 font-medium text-[14px] text-[var(--color-text-secondary)] hover:bg-[var(--color-bg-subtle-gray)]"
                >
                  キャンセル
                </button>
                <button
                  type="submit"
                  disabled={creating}
                  style={{
                    backgroundColor: creating ? "#9ca3af" : undefined,
                    cursor: creating ? "not-allowed" : "pointer",
                  }}
                  className={`rounded-md border-none px-4 py-2.5 font-medium text-[14px] text-white ${creating ? "" : "bg-[var(--color-primary-500)] hover:bg-[var(--color-primary-600)]"}`}
                >
                  {creating ? "作成中..." : "作成"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {editDialogOpen && editingInterviewer && (
        <div
          className="fixed inset-0 z-[1000] flex items-center justify-center bg-black/50"
          onClick={() => {
            setEditDialogOpen(false);
            setEditingInterviewer(null);
          }}
        >
          <div
            className="w-full max-w-[450px] rounded-xl bg-white p-6 shadow-[0_4px_20px_rgba(0,0,0,0.15)]"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="mb-5 font-semibold text-[18px] text-[var(--color-text-primary)]">
              インタビュアーを編集
            </h3>
            <form onSubmit={handleEditSave}>
              <div className="mb-4">
                <label className="mb-1.5 block font-medium text-[14px] text-[var(--color-text-primary)]">
                  メールアドレス
                </label>
                <p className="text-[14px] text-[var(--color-text-secondary)]">
                  {editingInterviewer.email}
                </p>
              </div>
              <div className="mb-4">
                <label
                  htmlFor="edit-name"
                  className="mb-1.5 block font-medium text-[14px] text-[var(--color-text-primary)]"
                >
                  名前
                </label>
                <input
                  id="edit-name"
                  type="text"
                  required
                  value={editFormData.name}
                  onChange={(e) =>
                    setEditFormData({ ...editFormData, name: e.target.value })
                  }
                  placeholder="山田 太郎"
                  className="box-border w-full rounded-md border border-[var(--color-border-default)] px-3 py-2.5 text-[14px] outline-none"
                />
              </div>
              <div className="mb-5">
                <label className="mb-2 block font-medium text-[14px] text-[var(--color-text-primary)]">
                  ロール
                </label>
                <div className="flex flex-col gap-2">
                  <label className="flex cursor-pointer items-center gap-2">
                    <input
                      type="radio"
                      name="edit-role"
                      checked={editFormData.role === "admin"}
                      onChange={() =>
                        setEditFormData({ ...editFormData, role: "admin" })
                      }
                      className="accent-[var(--color-primary-500)]"
                    />
                    <span className="text-[14px]">管理者</span>
                  </label>
                  <label className="flex cursor-pointer items-center gap-2">
                    <input
                      type="radio"
                      name="edit-role"
                      checked={editFormData.role === "interviewer"}
                      onChange={() =>
                        setEditFormData({
                          ...editFormData,
                          role: "interviewer",
                        })
                      }
                      className="accent-[var(--color-primary-500)]"
                    />
                    <span className="text-[14px]">インタビュアー</span>
                  </label>
                </div>
              </div>
              <div className="mb-5">
                <label className="flex cursor-pointer items-center gap-2">
                  <input
                    type="checkbox"
                    checked={editFormData.isDefault}
                    onChange={(e) =>
                      setEditFormData({
                        ...editFormData,
                        isDefault: e.target.checked,
                      })
                    }
                    className="accent-[var(--color-primary-500)]"
                  />
                  <span className="font-medium text-[14px]">
                    デフォルトインタビュアーに設定
                  </span>
                </label>
              </div>
              <div className="mb-5">
                <label className="flex cursor-pointer items-center gap-2">
                  <input
                    type="checkbox"
                    checked={editFormData.isKeyInterviewer}
                    onChange={(e) =>
                      setEditFormData({
                        ...editFormData,
                        isKeyInterviewer: e.target.checked,
                      })
                    }
                    className="accent-[var(--color-primary-500)]"
                  />
                  <span className="font-medium text-[14px]">
                    キーインタビュアーに設定
                  </span>
                </label>
              </div>
              <div className="flex justify-end gap-3">
                <button
                  type="button"
                  onClick={() => {
                    setEditDialogOpen(false);
                    setEditingInterviewer(null);
                  }}
                  className="cursor-pointer rounded-md border border-[var(--color-text-secondary)] bg-white px-4 py-2.5 font-medium text-[14px] text-[var(--color-text-secondary)] hover:bg-[var(--color-bg-subtle-gray)]"
                >
                  キャンセル
                </button>
                <button
                  type="submit"
                  disabled={editing}
                  style={{
                    backgroundColor: editing ? "#9ca3af" : undefined,
                    cursor: editing ? "not-allowed" : "pointer",
                  }}
                  className={`rounded-md border-none px-4 py-2.5 font-medium text-[14px] text-white ${editing ? "" : "bg-[var(--color-primary-500)] hover:bg-[var(--color-primary-600)]"}`}
                >
                  {editing ? "保存中..." : "保存"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <style>
        {`
          @keyframes spin {
            to { transform: rotate(360deg); }
          }
        `}
      </style>
    </div>
  );
}
